import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useBreadcrumbLabel } from '@/contexts/BreadcrumbContext';
import { DataTable } from '@/components/ui/data-table';
import type { Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  History,
  Loader2,
  UserPlus,
} from 'lucide-react';
import { migrationV1V2Api } from './api/migration-v1-v2';
import type { AutoCatchUpSweepDetail, AutoCatchUpSweepSchool } from './types';
import {
  formatDateTime,
  formatDuration,
  formatLotSummary,
  schoolOutcomePresentation,
  scopeLabel,
  triggerLabel,
} from './components/autoCatchUpPresentation';
import { StatusPill, SweepStatusBadge } from './components/AutoCatchUpPills';

/**
 * Détail d'une passe : ce que la tâche a fait, école par école. Les écoles
 * balayées sans rien à reprendre sont conservées (repliées par défaut) — c'est
 * la preuve de couverture de la passe, pas du bruit à masquer.
 */

function Metric({ label, value, tone }: { label: string; value: string | number; tone?: string }) {
  return (
    <div className="rounded-lg border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-3">
      <p className={cn('text-xl font-bold tabular-nums text-gray-900 dark:text-gray-100', tone)}>
        {value}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    </div>
  );
}

export function AutoCatchUpSweepPage() {
  const { sweepId } = useParams<{ sweepId: string }>();
  const navigate = useNavigate();

  const [sweep, setSweep] = useState<AutoCatchUpSweepDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUntouched, setShowUntouched] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useBreadcrumbLabel(sweep ? `Passe du ${formatDateTime(sweep.startedAt)}` : null);

  const load = useCallback(async () => {
    if (!sweepId) return;
    setLoading(true);
    try {
      const data = await migrationV1V2Api.getAutoCatchUpSweep(Number(sweepId));
      setSweep(data);
      setError(null);
    } catch {
      setError('Passe introuvable.');
    } finally {
      setLoading(false);
    }
  }, [sweepId]);

  useEffect(() => {
    void load();
  }, [load]);

  const touched = useMemo(
    () => (sweep?.schools ?? []).filter((s) => s.status !== 'nothing'),
    [sweep]
  );
  const untouchedCount = (sweep?.schools.length ?? 0) - touched.length;
  const rows = showUntouched ? sweep?.schools ?? [] : touched;

  const toggle = (projectId: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });

  const columns: Column<AutoCatchUpSweepSchool>[] = [
    {
      key: 'expand',
      header: '',
      className: 'w-10',
      render: (row) => {
        const hasDetail =
          row.additions.length > 0 ||
          row.lots.length > 0 ||
          !!row.error ||
          (row.outOfReach?.length ?? 0) > 0;
        if (!hasDetail) return null;
        return (
          <button
            type="button"
            onClick={() => toggle(row.projectId)}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            aria-label={expanded.has(row.projectId) ? 'Replier' : 'Déplier'}
          >
            {expanded.has(row.projectId) ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        );
      },
    },
    {
      key: 'school',
      header: 'École',
      render: (row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {row.name || row.code}
          </span>
          <Badge variant="outline" className="font-mono text-xs">
            {row.code}
          </Badge>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Résultat',
      className: 'w-56',
      render: (row) => <StatusPill {...schoolOutcomePresentation(row.status)} />,
    },
    {
      key: 'added',
      header: 'Éléments migrés',
      className: 'w-36 text-right',
      render: (row) =>
        row.added > 0 ? (
          <span className="inline-flex items-center gap-1 rounded-md bg-sky-100 px-2 py-0.5 text-sm font-semibold tabular-nums text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
            <UserPlus className="h-3 w-3" />
            {row.added}
          </span>
        ) : row.pending > 0 ? (
          <span className="text-sm text-violet-600 dark:text-violet-300 tabular-nums">
            {row.pending} en attente
          </span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        ),
    },
    {
      key: 'failed',
      header: 'Échecs',
      className: 'w-24 text-right',
      render: (row) =>
        row.failed > 0 ? (
          <span className="text-sm font-medium text-red-600 dark:text-red-400 tabular-nums">
            {row.failed}
          </span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        ),
    },
    {
      key: 'lots',
      header: 'Lots rejoués',
      render: (row) => {
        const played = row.lots.filter(
          (l) =>
            l.added > 0 ||
            (l.reassigned ?? 0) > 0 ||
            (l.preserved ?? 0) > 0 ||
            (l.attached ?? 0) > 0 ||
            l.failed > 0 ||
            l.error
        );
        if (played.length === 0) return <span className="text-sm text-gray-400">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {played.map((lot) => (
              <span
                key={lot.code}
                className={cn(
                  'rounded px-1.5 py-0.5 text-xs',
                  lot.error || lot.failed > 0
                    ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                    : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                )}
              >
                {formatLotSummary(lot)}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right w-40',
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/migration-v1-v2/rattrapage/ecoles/${row.projectId}`)}
        >
          <History className="h-4 w-4 mr-1" />
          Historique
        </Button>
      ),
    },
  ];

  const renderSubRow = (row: AutoCatchUpSweepSchool) => {
    if (!expanded.has(row.projectId)) return null;
    return (
      <div className="space-y-3 py-2">
        {row.error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
            {row.error}
          </div>
        )}
        {(row.outOfReach?.length ?? 0) > 0 && (
          <div className="rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-900 dark:border-orange-900 dark:bg-orange-950/30 dark:text-orange-200">
            <span className="font-medium">Hors rattrapage :</span> ces lots n'ont jamais
            été lancés alors que la V1 les remplit — leur première migration reste à
            faire à la main.
            <div className="mt-1 flex flex-wrap gap-1">
              {row.outOfReach?.map((code) => (
                <span
                  key={code}
                  className="rounded bg-orange-100 px-1.5 py-0.5 font-mono text-[11px] dark:bg-orange-900/40"
                >
                  {code}
                </span>
              ))}
            </div>
          </div>
        )}

        {row.lots
          .filter((lot) => lot.error)
          .map((lot) => (
            <div
              key={`err-${lot.code}`}
              className="rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-900 dark:border-orange-900 dark:bg-orange-950/30 dark:text-orange-200"
            >
              <span className="font-semibold">{lot.name}</span> — {lot.error}
            </div>
          ))}

        {row.additions.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border dark:border-slate-700">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 dark:bg-slate-800/60 text-gray-500">
                  <th className="text-left py-2 px-3 font-medium">Élément migré</th>
                  <th className="text-left py-2 px-3 font-medium">Lot</th>
                  <th className="text-left py-2 px-3 font-medium">Résultat</th>
                </tr>
              </thead>
              <tbody>
                {row.additions.map((addition, i) => (
                  <tr key={`${addition.lotCode}-${addition.sourceId}-${i}`} className="border-b last:border-b-0 dark:border-slate-700">
                    <td className="py-2 px-3 font-medium text-gray-800 dark:text-gray-200">
                      {addition.label}
                    </td>
                    <td className="py-2 px-3 text-slate-500 whitespace-nowrap">{addition.lotName}</td>
                    <td className="py-2 px-3">
                      {addition.status === 'migrated' ? (
                        <span className="text-green-700 dark:text-green-300">Migré</span>
                      ) : (
                        <span className="text-red-700 dark:text-red-300">
                          Échec{addition.error ? ` — ${addition.error}` : ''}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-slate-500">
            Aucun détail nominatif pour cette école sur cette passe.
          </p>
        )}

        {row.additionsTruncated && (
          <p className="text-xs text-gray-400">
            Liste limitée aux {row.additions.length} premiers éléments — les compteurs restent
            exacts.
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !sweep) {
    return (
      <div className="space-y-4">
        <Link to="/migration-v1-v2/rattrapage">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Retour
          </Button>
        </Link>
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error ?? 'Passe introuvable.'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="space-y-1">
          <Link to="/migration-v1-v2/rattrapage">
            <Button variant="ghost" size="sm" className="-ml-2">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Toutes les exécutions
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Passe du {formatDateTime(sweep.startedAt)}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {triggerLabel(sweep.trigger)} · {scopeLabel(sweep)}
            {sweep.triggeredBy &&
              ` · déclenchée par ${sweep.triggeredBy.firstname} ${sweep.triggeredBy.lastname}`}
            {sweep.dryRun && ' · simulation (aucune écriture)'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {sweep.status === 'running' && !sweep.interrupted && (
            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
          )}
          <SweepStatusBadge sweep={sweep} />
        </div>
      </div>

      {sweep.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
          {sweep.error}
        </div>
      )}

      {sweep.interrupted && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-800 dark:border-orange-900 dark:bg-orange-950/30 dark:text-orange-200">
          Cette passe n'a jamais été clôturée : elle a été coupée en vol (pod recyclé ou délai
          maximum atteint). Les écoles listées ci-dessous ont bien été traitées, les suivantes
          seront reprises à l'exécution suivante.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Metric
          label={
            sweep.projectsEligible > sweep.projectsScanned
              ? `Écoles balayées (sur ${sweep.projectsEligible} éligibles)`
              : 'Écoles balayées'
          }
          value={sweep.projectsScanned}
        />
        <Metric label="Écoles servies" value={sweep.projectsWithAdditions} />
        <Metric
          label="Éléments migrés"
          value={sweep.addedRecords}
          tone={sweep.addedRecords > 0 ? 'text-sky-600 dark:text-sky-300' : undefined}
        />
        <Metric
          label="Fiches en échec"
          value={sweep.failedRecords}
          tone={sweep.failedRecords > 0 ? 'text-red-600 dark:text-red-400' : undefined}
        />
        <Metric label="Durée" value={formatDuration(sweep.durationSeconds)} />
      </div>

      {untouchedCount > 0 && (
        <div className="flex items-center gap-2">
          <Switch id="show-untouched" checked={showUntouched} onCheckedChange={setShowUntouched} />
          <Label htmlFor="show-untouched" className="text-sm font-normal">
            Afficher les {untouchedCount} école{untouchedCount > 1 ? 's' : ''} balayée
            {untouchedCount > 1 ? 's' : ''} sans rien à reprendre
          </Label>
        </div>
      )}

      <DataTable
        columns={columns}
        data={rows}
        keyExtractor={(row) => row.projectId}
        renderSubRow={renderSubRow}
        emptyMessage={
          untouchedCount > 0
            ? "Cette passe n'a rien eu à reprendre — activez l'option ci-dessus pour voir les écoles balayées."
            : "Aucune école balayée par cette passe."
        }
      />
    </div>
  );
}
